if (!customElements.get("video-modal-controller"))
  customElements.define(
    "video-modal-controller",
    class extends HTMLElement {
      constructor() {
        super();

        this.isUpdating = false;

        this.modalId = this.getAttribute("data-modal");
        this.modal = document.getElementById(this.modalId);
        this.video = this.modal?.querySelector("video");

        this.items = this.querySelectorAll(".video-item");
        this.items.forEach((item) => {
          this.initItem(item);
        });

        this.onModalClosed = this.onModalClosed.bind(this);

        this.modal && this.modal.addEventListener("modalClosed", this.onModalClosed);
      }

      initItem(item) {
        const itemVideoJSON = item.querySelector(
          "script[type='application/json']"
        )?.textContent;
        if(!itemVideoJSON) return;

        let videoData = null;
        try {
          videoData = JSON.parse(itemVideoJSON);
        } catch (error) {
          console.error(" Failed to parse item sources: ", error);
          videoData = null;
        }

        if(!videoData) return;
        const playOverlay = item.querySelector(".play-overlay");

        Object.assign(item, {
          videoData,
          playOverlay,
          handleClick: () => {
            this.handleItemClick(item);
          },
        });

        playOverlay.addEventListener("click", item.handleClick);
      }

      handleItemClick(item) {
        const isModalOpen = this.modal.hasAttribute("open") || false;
        if (isModalOpen || this.isUpdating) return;
        
        this.isUpdating = true;
        item.classList.add("loading");
        this.updateVideoAttributes(item);
      }

      updateVideoAttributes(item) {
        if (!item.videoData || !this.video) return;
        this.updateVideo(item).catch((err) => {
          console.warn("Error updating video attributes:", err);
          this.modal.show();
          this.isUpdating = false;
        });
      }

      async updateVideo(item) {
        const { aspectRatio, poster: newPosterUrl, sources } = item.videoData;
        
        this.video.parentElement.style.setProperty("--aspect-ratio", aspectRatio);
        this.video.pause();
        this.video.currentTime = 0;
        
        const newSource = this.findVideoSource(sources);
        if (!newSource) return;
        
        this.updateVideoSource(newSource);
        this.updatePosterImage(newPosterUrl);
        
        this.video.load();
        
        await Promise.all([
          this.loadPosterImage(newPosterUrl),
          this.waitForVideoReady()
        ]);
        
        this.modal.show();
        requestAnimationFrame(async () => {
          try {
            await this.video.play();
          } catch (err) {
            console.warn("Video play failed:", err);
          }
        });
        item.classList.remove("loading");
        this.isUpdating = false;
      }

      findVideoSource(sources) {
        const source = this.video.querySelector("source");
        const currentMimeType = source?.type || null;
        return sources.find((s) => s.mimeType === currentMimeType) || sources[0];
      }

      updateVideoSource(newSource) {
        const source = this.video.querySelector("source");
        source.src = newSource.url;
        if (source.type !== newSource.mimeType) {
          source.type = newSource.mimeType;
        }
      }

      updatePosterImage(newPosterUrl) {
        if (!newPosterUrl) return;
        
        this.video.poster = newPosterUrl;
        const img = this.video.querySelector("img");
        if (img) img.src = newPosterUrl;
      }

      async loadPosterImage(posterUrl) {
        if (!posterUrl) return;
        
        return new Promise((resolve) => {
          const imageLoader = new Image();
          imageLoader.onload = resolve;
          imageLoader.onerror = resolve;
          imageLoader.src = posterUrl;
        });
      }

      async waitForVideoReady() {
        return new Promise((resolve) => {
          const onReady = () => {
            this.video.removeEventListener("loadeddata", onReady);
            this.video.removeEventListener("canplay", onReady);
            resolve();
          };
          this.video.addEventListener("loadeddata", onReady, { once: true });
          this.video.addEventListener("canplay", onReady, { once: true });
        });
      }

      onModalClosed() {
        this.video.pause();
        this.video.currentTime = 0;
      }

      disconnectedCallback() {
        this.items.forEach((item) => {
          if (item.playOverlay && item.handleClick && typeof item.handleClick === "function") {
            item.playOverlay.removeEventListener("click", item.handleClick);
          }
        });
        this.modal && this.modal.removeEventListener("modalClosed", this.onModalClosed);
      }
    }
  );
